import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { pluralizeToString } from 'lib/string/pluralize'
import { OfferPageRoomOption, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Pane from 'components/Common/Pane'
import usePropertyBedbankViews from 'hooks/OfferPage/usePropertyBedbankViews'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import useOffer from 'hooks/Offers/useOffer'
import PropertyRoomCheapestOptionMobile from 'components/OfferPage/Property/MobileBookingFlow/PropertyRoomCheapestOptionMobile'
import { getPackageOptionsByRoom } from 'components/OfferPage/Property/singlePropertyUtils'
import AccommodationMobileHeaderFilters from 'components/OfferPage/Accommodation/AccommodationMobileHeaderFilters'
import BodyText from 'components/Luxkit/Typography/BodyText'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
`

interface Props {
  offer: App.HotelOffer;
  packageOptions: Array<OfferPackageOptionView>;
  roomOption?: OfferPageRoomOption;
  onBack: () => void;
  onClose: () => void;
}

function SinglePropertyRoomSelectionModal(props: Props) {
  const {
    offer,
    packageOptions,
    roomOption,
    onBack,
    onClose,
  } = props

  const hasRoomOption = roomOption && Object.entries(roomOption).length === 2

  const [bedbankPackageViews, fetchingBedbankViews] = usePropertyBedbankViews(offer.property.id)
  const [mapping] = usePropertyOfferMapping(offer.property.id)
  const [bedbankOffer] = useOffer<App.BedbankOffer>(mapping?.bedbankOfferId)

  // Get the mapping for this property page so we know what bedbank offer to fetch
  const packageOptionsByRoom = useMemo(() => getPackageOptionsByRoom(packageOptions, bedbankPackageViews, mapping), [packageOptions, bedbankPackageViews, mapping])

  return (
    <>
      <ModalHeader
        title="Select a room"
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <AccommodationMobileHeaderFilters />
      </ModalHeader>
      <ModalBody>
        <>
          {packageOptionsByRoom.length > 1 && (
            <OptionsContainer>
              <BodyText variant="small" colour="neutral-three">
                {pluralizeToString('room type', packageOptionsByRoom.length)} available
              </BodyText>
            </OptionsContainer>
          )}
          <Pane background="neutral-six">
            <VerticalSpacer gap={8}>
              {packageOptionsByRoom.map((item) => {
                if (hasRoomOption && roomOption.roomTypeId === item.roomId) {
                  return null
                }
                return (<PropertyRoomCheapestOptionMobile
                  key={item.roomId}
                  offer={offer}
                  packageViews={item.lpc}
                  bedbankViews={item.bedbank}
                  bedbankOffer={bedbankOffer}
                  fetchingBedbankViews={fetchingBedbankViews}
                  detailsModalMode="room-only"
                />)
              })}
            </VerticalSpacer>
          </Pane>
        </>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  offer: state.offer,
  rooms: state.rooms,
  roomOption: state.roomOption,
  packageOptions: getDurationPackageOptions(state, appState),
}))(SinglePropertyRoomSelectionModal)
